import { useState } from 'react';
import {
  updateMealCreditPeople,
  UpdateMealCreditPeoplePayload,
} from 'src/components/oneCorp/features/MealCredit/hooks/updateMealCreditPeopleApi';
import { useAsyncTaskHandler } from 'src/common/utils/useAsyncTaskHandler';
import { LONG_SEARCH_DELAY } from 'src/utilities/useSearchHooks';

export interface UseManageMealCreditDinersProps {
  corpId: string;
  mealCreditId: string;
  onSuccess?: () => void;
  onError?: () => void;
}

export interface UseManageMealCreditDiners {
  isAdding: boolean;
  isRemoving: boolean;
  removePeople: (dinersToRemove: string[]) => Promise<void>;
  addPeople: (dinersToAdd: string[]) => Promise<void>;
  status: ManageMealCreditDinerStatus;
}

export enum ManageMealCreditDinerStatus {
  IDLE,
  ASSIGNING,
  REMOVING,
}

export const useManageMealCreditDiners = ({
  corpId,
  mealCreditId,
  onError,
  onSuccess,
}: UseManageMealCreditDinersProps): UseManageMealCreditDiners => {
  const { startAsyncTask } = useAsyncTaskHandler({
    onSuccess: handleSuccess,
    onError: handleError,
  });
  const [status, setStatus] = useState<ManageMealCreditDinerStatus>(
    ManageMealCreditDinerStatus.IDLE
  );
  const isAdding = status === ManageMealCreditDinerStatus.ASSIGNING;
  const isRemoving = status === ManageMealCreditDinerStatus.REMOVING;

  async function handleSuccess(): Promise<void> {
    /**
     * The reason for this timeout is that elastic search needs some time to rearrange the indexes
     * after a record is deleted. Without this timeout the search could potentially
     * return outdated results
     */
    await new Promise((resolve) =>
      setTimeout(() => {
        onSuccess();
        setStatus(ManageMealCreditDinerStatus.IDLE);
        resolve('');
      }, LONG_SEARCH_DELAY)
    );
  }

  async function handleError(): Promise<void> {
    onError?.();
    setStatus(ManageMealCreditDinerStatus.IDLE);
  }

  const updatePeople = async (payload: UpdateMealCreditPeoplePayload) => {
    await startAsyncTask(() => {
      return updateMealCreditPeople({ corpId, mealCreditId, payload });
    });
  };

  const removePeople = async (dinersToRemove: string[]) => {
    setStatus(ManageMealCreditDinerStatus.REMOVING);
    await updatePeople({ diners_to_add: [], diners_to_remove: dinersToRemove });
  };

  const addPeople = async (dinersToAdd: string[]) => {
    setStatus(ManageMealCreditDinerStatus.ASSIGNING);
    await updatePeople({ diners_to_add: dinersToAdd, diners_to_remove: [] });
  };

  return {
    status,
    isAdding,
    isRemoving,
    removePeople,
    addPeople,
  };
};
